/**
 * Global Utility to format and save a notification to localStorage.
 * Shared by WebSockets, Push notifications, and direct app events.
 */
export function StoreNotifications({
  receivedData,
  ixBiz,
  currentUser,
  storeNotifications = true,
  specificUserNotif = true
}) {
  if (!storeNotifications) return;

  // 1. Enforce business isolation boundaries to prevent cross-tenant data leaks
  const bizScope = ixBiz ?? receivedData?.biz ?? receivedData?.ixBiz ?? '';
  if (!bizScope) {
    return; // Silently reject if tenant context is completely missing
  }

  // 2. Separate text extraction rules based on custom payload signatures
  // If type is "Notif", evaluate .text strictly to bypass stringified JSON inside .message
  const isExplicitNotifType = receivedData?.type === 'Notif';
  const rawMessage = isExplicitNotifType
    ? (receivedData?.NotifText || receivedData?.text) ?? ''
    : receivedData?.message ?? receivedData?.msg ?? receivedData?.text ?? '';

  if (
    !rawMessage ||
    rawMessage.trim() === '' ||
    receivedData?.type === 'chat'
  ) {
    return;
  }

  const currentUsernameLower = currentUser?.username?.toLowerCase() || '';

  // 3. Explicit "Notif" Command Guard & Target Target Rule Processing
  const payloadForcedStore = receivedData?.storeNotif ?? true;
  const explicitTarget = receivedData?.targetUser
    ? receivedData.targetUser.trim().toLowerCase()
    : null;

  const hasAssignmentList = Boolean(
    receivedData?.userList || receivedData?.assignedTo
  );
  // General broadcast alert if targetUser is omitted, otherwise verify it belongs to me
  const isTargetedToMe =
    // 1. If an explicit target user is set, it must be me
    (explicitTarget && explicitTarget === currentUsernameLower) ||
    // 2. If no explicit target is set AND no assignment list exists, it's a broadcast
    (!explicitTarget && !hasAssignmentList) ||
    // 3. If an assignment list exists, defer strictly to the assignment check below
    (!explicitTarget && hasAssignmentList && isAssignedToCurrentUser);

  const isExplicitNotifMatch =
    isExplicitNotifType && payloadForcedStore && isTargetedToMe;

  // 4. Legacy Workflow Assigned Multi-User Validation Routing
  const isAssignedToCurrentUser =
    receivedData?.username !== currentUser?.username &&
    ['assignedTo', 'userList'].some(key => {
      const value = receivedData?.[key];
      if (!value) return false;

      if (Array.isArray(value)) {
        return value
          .map(v => v.trim().toLowerCase())
          .includes(currentUsernameLower);
      }

      if (typeof value === 'string') {
        return value
          .split(',')
          .map(v => v.trim().toLowerCase())
          .includes(currentUsernameLower);
      }
      return false;
    });

  const isAllowedToSave =
    (!specificUserNotif && receivedData?.type !== 'Assigned_Update') ||
    (receivedData?.type === 'chat' &&
      currentUsernameLower !== receivedData?.username?.toLowerCase());

  // Gate Keeper: Bypasses standard filters if it's an explicitly matched targeted Notif event
  if (!(isExplicitNotifMatch || isAllowedToSave || isAssignedToCurrentUser))
    return;

  // 5. Collision Prevention via Client-Side Sequential ID Generation
  let baseId = Date.now();
  let storedNotifications =
    JSON.parse(localStorage.getItem('Notifications')) || {};

  // Safely loop step increments if multiple actions process in the exact same millisecond
  while (storedNotifications[baseId.toString()]) {
    baseId++;
  }
  const finalId = baseId.toString();

  // 6. Throttling frequent duplicate saves (300ms window)
  const lastTimestamp = Math.max(
    ...Object.keys(storedNotifications).map(Number),
    0
  );
  if (baseId - lastTimestamp < 300) return;

  // 7. Build Normalized Sender Metadata
  const senderName = receivedData?.sender || receivedData?.username || null;
  let senderType = receivedData?.senderType || null;

  // Default Fallback: If sender name is present but has no specified type, classify as system
  if (senderName && !senderType) {
    senderType = 'system';
  }

  // 8. Assemble Unified Structural Contract Shape
  const MAX_LENGTH = 30;
  const bodyText =
    rawMessage.length > MAX_LENGTH
      ? rawMessage.slice(0, MAX_LENGTH) + '...'
      : rawMessage;

  const hasPath = Boolean(receivedData?.path);

  // Capitalize the display title category (Uses variant field e.g., "Info", "Warning", or event Type)
  const titleCategory =
    receivedData?.OverrideTypeLabel ??
    receivedData?.variant ??
    receivedData?.type ??
    'info';
  const formattedTitle =
    titleCategory.charAt(0).toUpperCase() +
    titleCategory.slice(1).replace('_', ' ');

  const unifiedNotification = {
    id: finalId,
    biz: bizScope,
    read: false,
    type: receivedData?.type || 'workflow',
    title: formattedTitle,
    body: bodyText,
    sender: senderName,
    senderType: senderType,
    action: {
      hasAction: hasPath,
      path: hasPath ? receivedData.path : null
      //   label: hasPath ? 'View Details' : null
    }
  };

  // 9. Cap Capacity Queue Enforcement
  const timestamps = Object.keys(storedNotifications)
    .map(Number)
    .sort((a, b) => a - b);
  if (timestamps.length >= 5) {
    delete storedNotifications[timestamps[0].toString()];
  }

  // 10. Persist to storage layer and dispatch real-time state synchronizations
  storedNotifications[finalId] = unifiedNotification;
  localStorage.setItem('Notifications', JSON.stringify(storedNotifications));
  window.dispatchEvent(new Event('Notifications'));
}
